import { Injectable } from "@angular/core";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import { AsyncSubject, Observable } from "rxjs";

const API_URL = API.API_URL;

/**
 * Validates access to the application through the HUB database.
 *
 * @author			John Ying <john.ying@childrenshospitals.org>
 * @created			9/22/2022
 * @modifiedBy		John Ying <john.ying@childrenshospitals.org>
 * @modifiedDate	6/8/2023
 */
@Injectable({
	providedIn: "root"
})
export class AuthorizationService {
	permissionMap = new Map<string, AsyncSubject<boolean>>();
	groupMap = new Map<string, AsyncSubject<boolean>>();

	constructor(private httpManager: HttpManager) {}

	/**
	 * Calls the HUB database to check whether the user ID has access to the given permission.
	 *
	 * @param userID      - the user ID
	 * @param permission  - the permission to validate
	 *
	 * @returns true if the validation is a success, otherwise false
	 */
	validatePermission(userID: string, permission: string): Observable<boolean> {
		const query = {
			userID: userID,
			key: permission,
			validationType: "permission",
			params: { userID: userID, permission: permission }
		};
		return this.validateAuthorization(query, this.permissionMap);
	}

	/**
	 * Calls the HUB database to check whether the user ID is a member of the given group.
	 *
	 * @param userID  - the user ID
	 * @param group   - the group to validate
	 *
	 * @returns true if the validation is a success, otherwise false
	 */
	validateGroup(userID: string, group: string): Observable<boolean> {
		const query = {
			userID: userID,
			key: group,
			validationType: "group",
			params: { userID: userID, group: group }
		};
		return this.validateAuthorization(query, this.groupMap);
	}

	private validateAuthorization(
		query: any,
		authorizationMap: Map<string, AsyncSubject<boolean>>
	): Observable<boolean> {
		if (authorizationMap.has(query.key)) {
			return authorizationMap.get(query.key);
		}

		const newValidation = new AsyncSubject<boolean>();
		authorizationMap.set(query.key, newValidation);
		this.httpManager
			.httpGet<boolean>(`${API_URL}/security/validate/` + query.validationType, query.params)
			.subscribe(data => {
				newValidation.next(data);
				newValidation.complete();
			});
		return newValidation;
	}
}

